import { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { ChevronDown, Menu, X } from 'lucide-react';
import { useAuthStore } from '@shared/state';
import { signOut } from '@shared/services/auth';
import type { User } from '@shared/types';
import { XPCounter } from '../../gamification/XPCounter';
import { StreakBadge } from '../../gamification/StreakBadge';

export function AuthenticatedHeader() {
  const user = useAuthStore((s) => s.user);
  const navigate = useNavigate();
  const location = useLocation();
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 10);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    setMenuOpen(false);
    setMobileOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!menuOpen) return;
    function onClick(e: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpen(false);
      }
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setMenuOpen(false);
    }
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [menuOpen]);

  useEffect(() => {
    if (!mobileOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [mobileOpen]);

  const handleSignOut = async () => {
    await signOut();
    setMobileOpen(false);
    setMenuOpen(false);
    navigate('/');
  };

  if (!user) return null;

  const isPaid = user.subscription && user.subscription !== 'free';
  const isTeamAdmin = !!(user as { teamAdminOf?: string | null }).teamAdminOf;
  // Site admins reach the admin portal (/admin-dashboard) only by URL otherwise —
  // the "Dashboard" link always goes to the user dashboard. Surface a link.
  const isSiteAdmin = user.role === 'admin';

  return (
    <nav
      className={`sticky top-0 z-50 transition-[background-color,border-color,backdrop-filter] duration-300 ${
        scrolled
          ? 'bg-dark-bg/80 backdrop-blur-sm border-b border-dark-border'
          : 'bg-transparent border-b border-transparent'
      }`}
    >
      <div className="px-2 sm:px-3 lg:px-4">
        <div className="flex items-center justify-between h-24">
          <Link to="/" className="flex items-center">
            <img
              src="/logo-horizontal.png"
              alt="CogniPrint"
              className="h-12 sm:h-16 w-auto"
            />
          </Link>

          <div className="hidden md:flex items-center gap-5">
            <Link to="/how-it-works" className="text-sm text-content-secondary hover:text-content transition-colors font-body">
              How It Works
            </Link>
            <Link to="/pricing" className="text-sm text-content-secondary hover:text-content transition-colors font-body">
              Pricing
            </Link>
            <Link to="/for-teams" className="text-sm text-content-secondary hover:text-content transition-colors font-body">
              For Teams
            </Link>
            <Link to="/dashboard" className="text-sm text-content-secondary hover:text-content transition-colors font-body">
              Dashboard
            </Link>
            {isSiteAdmin && (
              <Link to="/admin-dashboard" className="text-sm text-indigo-300 hover:text-indigo-200 transition-colors font-body">
                Admin
              </Link>
            )}

            <StreakBadge userId={user.uid} />
            <XPCounter userId={user.uid} />

            <div ref={menuRef} className="relative">
              <button
                type="button"
                onClick={() => setMenuOpen((v) => !v)}
                aria-haspopup="menu"
                aria-expanded={menuOpen}
                className="flex items-center gap-2 pl-1 pr-2 py-1 rounded-full border border-dark-border hover:border-indigo-400/40 hover:bg-dark-hover transition-colors cursor-pointer"
              >
                <UserAvatar user={user} />
                <span className="text-sm font-body font-semibold text-content max-w-[10rem] truncate">
                  {userLabel(user)}
                </span>
                <ChevronDown size={14} className={`text-content-secondary transition-transform ${menuOpen ? 'rotate-180' : ''}`} />
              </button>

              {menuOpen && (
                <div
                  role="menu"
                  className="absolute right-0 top-full mt-2 w-72 rounded-xl border border-dark-border bg-dark-surface shadow-modal overflow-hidden"
                >
                  <div className="px-4 py-3 border-b border-dark-border flex items-center gap-3">
                    <UserAvatar user={user} size={40} />
                    <div className="min-w-0">
                      <div className="text-sm font-body font-semibold text-content truncate">{userLabel(user)}</div>
                      <div className="text-xs font-body text-content-muted truncate">{user.email}</div>
                    </div>
                  </div>
                  <MenuItem to="/profile" label="Profile & Billing" helper="Your 8 dimensions + account" />
                  <MenuItem to="/profile/progress" label="Progress" helper="Streak, level and achievements" />
                  <MenuItem to="/profile/evolution" label="Profile Evolution" helper="How your profile has shifted" />
                  <MenuItem to="/profile/adaptations" label="Adaptation History" helper="Changes from feedback & recalibration" />
                  {isPaid && (
                    <MenuItem to="/monthly-report" label="Monthly Report" helper="Your coaching report" />
                  )}
                  {isTeamAdmin && (
                    <MenuItem to="/team" label="Team" helper="Manage members & invites" />
                  )}
                  {isSiteAdmin && (
                    <>
                      <div className="h-px bg-dark-border" />
                      <MenuItem to="/admin-dashboard" label="Admin Portal" helper="Content Lab, users, analytics & flags" />
                    </>
                  )}
                  <button
                    type="button"
                    onClick={handleSignOut}
                    className="w-full text-left px-4 py-3 border-t border-dark-border hover:bg-dark-hover transition-colors text-sm font-body font-semibold text-content cursor-pointer"
                  >
                    Sign Out
                  </button>
                </div>
              )}
            </div>
          </div>

          <button
            type="button"
            onClick={() => setMobileOpen((v) => !v)}
            aria-label={mobileOpen ? 'Close menu' : 'Open menu'}
            aria-expanded={mobileOpen}
            className="md:hidden w-10 h-10 flex items-center justify-center rounded-lg text-content-secondary hover:text-content hover:bg-dark-surface transition-colors cursor-pointer"
          >
            {mobileOpen ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      {mobileOpen && (
        <>
          <div
            className="fixed inset-0 top-24 z-40 bg-black/60 backdrop-blur-sm md:hidden"
            onClick={() => setMobileOpen(false)}
          />
          <div className="fixed inset-x-0 top-24 z-50 border-b border-dark-border bg-dark-surface md:hidden max-h-[calc(100vh-6rem)] overflow-y-auto">
            <div className="px-4 py-4 flex flex-col gap-1">
              <div className="px-3 py-3 flex items-center gap-3 border-b border-dark-border mb-2">
                <UserAvatar user={user} size={40} />
                <div className="min-w-0">
                  <div className="text-sm font-body font-semibold text-content truncate">{userLabel(user)}</div>
                  <div className="text-xs font-body text-content-muted truncate">{user.email}</div>
                </div>
              </div>
              <MobileLink to="/how-it-works" label="How It Works" />
              <MobileLink to="/pricing" label="Pricing" />
              <MobileLink to="/for-teams" label="For Teams" />
              <div className="h-px bg-dark-border my-2" />
              <MobileLink to="/dashboard" label="Dashboard" />
              <MobileLink to="/profile" label="Profile & Billing" helper="Your 8 dimensions + account" />
              <MobileLink to="/profile/progress" label="Progress" helper="Streak, level and achievements" />
              <MobileLink to="/profile/evolution" label="Profile Evolution" helper="How your profile has shifted" />
              <MobileLink to="/profile/adaptations" label="Adaptation History" helper="Changes from feedback & recalibration" />
              {isPaid && (
                <MobileLink to="/monthly-report" label="Monthly Report" helper="Your coaching report" />
              )}
              {isTeamAdmin && (
                <MobileLink to="/team" label="Team" helper="Manage members & invites" />
              )}
              {isSiteAdmin && (
                <MobileLink to="/admin-dashboard" label="Admin Portal" helper="Content Lab, users, analytics & flags" />
              )}
              <div className="h-px bg-dark-border my-2" />
              <button
                onClick={handleSignOut}
                className="w-full text-left px-3 py-3 rounded-lg text-sm text-content-secondary hover:text-content hover:bg-dark-hover transition-colors cursor-pointer font-body"
              >
                Sign Out
              </button>
            </div>
          </div>
        </>
      )}
    </nav>
  );
}

function userLabel(user: User): string {
  const name = user.displayName?.trim();
  if (name) return name;
  return user.email.split('@')[0];
}

const FALLBACK_AVATAR = '/fingerprint-logo-transparent.png';

function UserAvatar({ user, size = 32 }: { user: User; size?: number }) {
  const [errored, setErrored] = useState(false);
  const dim = `${size}px`;
  const src = user.photoURL && !errored ? user.photoURL : FALLBACK_AVATAR;
  return (
    <img
      src={src}
      alt={userLabel(user)}
      referrerPolicy="no-referrer"
      onError={() => setErrored(true)}
      style={{ width: dim, height: dim }}
      className="rounded-full object-cover border border-dark-border bg-dark-surface"
    />
  );
}

function MenuItem({ to, label, helper }: { to: string; label: string; helper: string }) {
  return (
    <Link
      to={to}
      role="menuitem"
      className="block px-4 py-3 hover:bg-dark-hover transition-colors"
    >
      <div className="text-sm font-body font-semibold text-content">{label}</div>
      <div className="text-xs font-body text-content-muted mt-0.5">{helper}</div>
    </Link>
  );
}

function MobileLink({ to, label, helper }: { to: string; label: string; helper?: string }) {
  return (
    <Link
      to={to}
      className="block px-3 py-3 rounded-lg hover:bg-dark-hover transition-colors"
    >
      <div className="text-sm font-body font-semibold text-content">{label}</div>
      {helper && <div className="text-xs font-body text-content-muted mt-0.5">{helper}</div>}
    </Link>
  );
}
